import * as util from 'util';
import _ from 'lodash';
import {GeneralService} from '../../../../services/generalService';
import {RouteService} from '../../../../services/routeService';
import {NotificationService} from '../../../../services/notificationService';
import {DICTIONARY} from '../../../../dictionary';
import {Component, Input, OnDestroy, OnInit} from "@angular/core";
import {ActivatedRoute, Router} from "@angular/router";
import {ClipboardService} from "ngx-clipboard";
import {connectDomain, subscribe} from 'entrijs';

@Component({
	selector: 'plan-settings-domain-component',
	templateUrl: './plan-settings-domain.component.html',
})
export class PlanSettingsDomainComponent implements OnInit, OnDestroy {
    @Input() hidePageTitle = false;
    dic = DICTIONARY;
    stopActiveDomainDelete = false;
    loadingDomainsInProcess = true;
    assignAdmins = ['your plan'];
    userInfo: any;
    isPartnerAdmin: any;
    planAdmins: any;
    userDomains: any;
    domain: any;
    amazonVerificationPopup: any;
    assignDomainToAdminPopup: any;
    assignToAdminInProcess: any;
    entriUnsubscribers = [];
    filterData;
	addActiveDomainPopup;
	unassignFromAdminPopup;
	isAdminCenter = false;
	reverifyAllInProcess = false;
	spfEntriPopup: any; // SPF auto-add (#15531) - separate from the SES "Auto Verify" flow
	mxChangePopup: any; // MX change through Entri - inbound domains page only, separate from every other domain action
	isInboundPage = false;
	private verifyRunId = 0; // bumped on every load / re-verify so a stale sequential chain stops

    constructor(private gs:GeneralService,
				private rs:RouteService,
				private ns:NotificationService,
				private clipboard: ClipboardService,
				private route: ActivatedRoute,
				private router: Router) {
    }

	ngOnDestroy() {
		// CC: stop any in-flight verifyDomainsOnLoad / reverifyAllDomains loop
		this.verifyRunId++;
	}

	ngOnInit() {
		this.isAdminCenter = this.route.parent?.snapshot.routeConfig?.path === this.dic.CONSTANTS.appStates.adminCenter;
		this.isInboundPage = this.router.url.includes('/' + this.dic.CONSTANTS.appStates.adminInbound + '/');
        this.gs.getUserInfo(false,  (userInfo) => {
            this.userInfo = userInfo;
            this.isPartnerAdmin = this.gs.isPartnerAdmin(this.userInfo);
        });
        this.initFilters();
        this.getDomains();
    };

    getDomains = () => {
        this.loadingDomainsInProcess = true;
        this.rs.getPlanDomains().then((response) => {
            this.userDomains = response;

            this.userDomains.forEach((domainObj) => {
                if (domainObj.assigned_to && !this.assignAdmins.includes(domainObj.assigned_to)) {
                    this.assignAdmins.push(domainObj.assigned_to);
                }
            });

            this.loadingDomainsInProcess = false;
            this.verifyDomainsOnLoad();
        }, (err) => {
			this.loadingDomainsInProcess = false;
		});
    };

	// Silent re-check on load, one domain at a time (the SES-backed checks share an account-wide 1 req/sec limit).
	// Identity / DKIM / MAIL FROM: only records that are not verified yet (SES keeps those statuses current, a nightly job resets drift).
	// SPF: EVERY domain, verified ones included - it is a cheap DNS-only check against the root domain's SPF record and
	// a customer can drop include:_spf.trustifi.com from that record at any time.
	verifyDomainsOnLoad = async () => {
		const runId = ++this.verifyRunId;
		for (const domainObj of this.userDomains || []) {
			if (runId !== this.verifyRunId) {
				return;
			}
			if (!domainObj.verified) {
				await this.isDomainIdentityVerified(domainObj);
			}
			if (!domainObj.dkim_verified) {
				await this.isDomainDkimVerified(domainObj);
			}
			if (!domainObj.mail_from_verified) {
				await this.isDomainMailFromVerified(domainObj);
			}
			await this.isDomainSpfVerified(domainObj);
		}
	};

	// "Re-verify All": real-time check of all four records for every domain, one domain at a time
	reverifyAllDomains = async () => {
		if (this.reverifyAllInProcess || this.loadingDomainsInProcess || !this.userDomains?.length) {
			return;
		}
		this.reverifyAllInProcess = true;
		const runId = ++this.verifyRunId;
		try {
			for (const domainObj of this.userDomains) {
				if (runId !== this.verifyRunId) {
					return;
				}
				await this.isDomainIdentityVerified(domainObj);
				await this.isDomainDkimVerified(domainObj);
				await this.isDomainMailFromVerified(domainObj);
				await this.isDomainSpfVerified(domainObj);
			}
		}
		finally {
			// also when a table refresh cancelled this run - a newer "Re-verify All" cannot start while the flag is set
			this.reverifyAllInProcess = false;
		}
	};

	// one verification call - no toasts, the status is shown on the page. A failed request (network, server error)
	// resolves to null and leaves the current status untouched - only a real true/false answer updates the row
	private verifyDomainRecord = (domainObj, type) => {
		return this.rs.isDomainVerified(domainObj.domain, {type}).then(isVerified => isVerified === true, () => null);
	};

    copyText(copy) {
        this.clipboard.copy(copy);
    };

    // CC: taken from inbound-mailboxes.component.ts -> toggleExpandItem
    toggleDomainExpand = (item, event) => {
        item.isExpanded = !item.isExpanded;
        event.stopPropagation();
    };

    addActiveDomain(domainName = "", err = "") {
        this.domain = domainName || '';

		this.addActiveDomainPopup = {
			show: true,
			applyInProcess: false,
			domainsName: this.domain,
			errMsg: err,
			autoVerify: false,
		};
    };

    showDomainActions = (domainObj) => {
        let actions:any = [
			this.dic.CONSTANTS.domainsActions.verifyEntri,
            this.dic.CONSTANTS.domainsActions.showRecords,
        ];

		if (!domainObj.spf_verified) {
			actions.push(this.dic.CONSTANTS.domainsActions.addSpfRecord);
		}

        if (this.isPartnerAdmin && domainObj.verified && !domainObj.assigned_to) {
            actions.push(this.dic.CONSTANTS.domainsActions.assignDomain);
        }
        if (this.isPartnerAdmin && domainObj.assigned_to) {
            actions.push(this.dic.CONSTANTS.domainsActions.unassignDomain);
        }

		// inbound page only: move the MX from a security gateway back to the mail platform
		if (this.isInboundPage) {
			actions.push(this.dic.CONSTANTS.domainsActions.changeMxRecord);
		}

        actions.push(this.dic.CONSTANTS.domainsActions.delete);

        return actions;
    }

    showBulkDomainsActions = () => {
        return [
            this.dic.CONSTANTS.domainsActions.delete
        ]
    }

    selectDomainAction = (domainObj, action) => {
        switch (action.value) {
            case this.dic.CONSTANTS.domainsActions.showRecords.value:
                // CC: expand the table row to reveal the DNS records instead of opening a popup
                domainObj.isExpanded = true;
                break;

            case this.dic.CONSTANTS.domainsActions.amazonVerifyInstructions.value:
                this.openAmazonVerificationPopup(domainObj);
                break;

            case this.dic.CONSTANTS.domainsActions.autoVerify.value:
                this.connectDomain(domainObj);
                break;

			case this.dic.CONSTANTS.domainsActions.verifyEntri.value:
				this.connectEntriDomain(domainObj);
				break;

			case this.dic.CONSTANTS.domainsActions.addSpfRecord.value:
				this.openSpfEntriPopup(domainObj);
				break;

			case this.dic.CONSTANTS.domainsActions.changeMxRecord.value:
				this.openMxChangePopup(domainObj);
				break;

            case this.dic.CONSTANTS.domainsActions.assignDomain.value:
                this.openAssignDomainToAdminPopup(domainObj);
                break;

            case this.dic.CONSTANTS.domainsActions.unassignDomain.value:
                this.unassignFromAdmin(domainObj);
                break;

            case this.dic.CONSTANTS.domainsActions.delete.value:
                this.openDeleteDomainPopup([domainObj]);
                break;
        }
    }

    selectMultipleDomainsAction = (selectedDomains,action) => {
        switch (action) {
            case this.dic.CONSTANTS.domainsActions.delete:
                this.openDeleteDomainPopup(selectedDomains);
                return;
        }
    }

    openDeleteDomainPopup = domains => {
        let title, subTitle, body;
        if (domains.length > 1) {
            title = 'Delete Domains';
            subTitle = `Please note - you are about to delete ${domains.length} domains`;
            body = ['Selected domains will no longer be verified', 'Users under the plan of these domains will become unverified',`Domains that are assigned to other plans will not be deleted from those plans`];
        }
        else {
            title = 'Delete Domain';
            subTitle = `Please note - you are about to delete ${domains[0].domain}`;
            body = ['Domain will no longer be verified', 'Users under the plan of this domain will become unverified'];
            if (domains[0].assigned_to) {
                body.push(`Domain will not be deleted from ${domains[0].assigned_to}'s plan`);
            }
        }

        this.gs.showPopup({
            title: title,
            subTitle: subTitle,
            body: body,
            type: this.dic.CONSTANTS.popupWarning,
            doneBtnText: 'Delete',
            doneCb: () => {
                domains.forEach((domainObj) => {
                    this.stopActiveDomainDelete = true;
                    this.rs.deleteDomain(domainObj.domain).then(() => {
                        _.remove<any>(this.userDomains, domainItem => domainItem.domain === domainObj.domain);
                        this.ns.showInfoMessage(this.dic.MESSAGES.domainDeleted);
                    });
                });

                this.stopActiveDomainDelete = false;
            }
        });
    };

    connectDomain = (domainObj) => {
        if (!domainObj || !domainObj.domain) {
            return;
        }

        const assignData = {
            action: this.dic.CONSTANTS.domainsActions.autoVerify.value
        };

        this.rs.updateDomain(domainObj.domain, assignData).then(data => {
            window.open(data.url, '_blank', 'location=no,width=750,height=600,scrollbars=no,resizable=no');
        });
    };

	connectEntriDomain = (domainObj) => {
		if (!domainObj || !domainObj.domain) {
			return;
		}

		// CC: entrijs "subscribe" is global - drop listeners from a previous run so they don't stack up and fire multiple times
		this.unsubscribeEntriEvents();

		this.entriUnsubscribers.push(subscribe('onSuccess', (event) => {
			if (event.domain !== domainObj.domain) {
				return;
			}

			if (!event.success) {
				this.ns.showWarnMessage(util.format(this.dic.ERRORS.domainNotVerified, domainObj.domain));
				return;
			}

			const assignData = {
				action: this.dic.CONSTANTS.domainsActions.updateProvider.value,
				dns_provider: event.provider,
				// CC: setupType can be null (see entrijs types)
				auto_update: (event.setupType || '').toLowerCase() === 'automatic'
			};
			this.rs.updateDomain(domainObj.domain, assignData).then(() => {
				this.ns.showInfoMessage(util.format(this.dic.MESSAGES.domainVerified, domainObj.domain));
			}, err => {
				if (err.data && err.data.message && !err.data.display_bar) {
					this.ns.showErrorMessage(err.data.message);
				}
			});
		}));

		this.entriUnsubscribers.push(subscribe('onEntriClose', () => {
			this.unsubscribeEntriEvents();
		}));

		this.entriUnsubscribers.push(subscribe('entriConfigError', () => {
			this.ns.showWarnMessage(util.format(this.dic.ERRORS.domainNotVerified, domainObj.domain));
			this.unsubscribeEntriEvents();
		}));

		const assignData = {
			action: this.dic.CONSTANTS.domainsActions.verifyEntri.value
		};

		this.rs.updateDomain(domainObj.domain, assignData).then(entriConfig => {
			//https://developers.entri.com/connect/configuration
			connectDomain({
				applicationId: entriConfig.applicationId,
				token: entriConfig.token,
				dnsRecords: entriConfig.dnsRecords,
				prefilledDomain: domainObj.domain,
				applicationName: 'Trustifi',
				// must equal the userId the backend signed the token with - a mismatch makes Entri skip the
				// automatic flow and open the manual screen right away (#15531 QA, GoDaddy)
				userId: entriConfig.userId,
				checkConflicts: true,
				useNewUI: true
			}).catch(err => {
				this.ns.showWarnMessage(util.format(this.dic.ERRORS.domainNotVerified, domainObj.domain));
				this.unsubscribeEntriEvents();
			});
		}, err => {
			this.unsubscribeEntriEvents();
			if (err.data && err.data.message && !err.data.display_bar) {
				this.ns.showErrorMessage(err.data.message);
			}
		});
	};

	// CC: entrijs subscribe() resolves to an unsubscribe function - keep them so listeners can be removed
	unsubscribeEntriEvents = () => {
		const pending = this.entriUnsubscribers;
		this.entriUnsubscribers = [];
		pending.forEach(p => {
			Promise.resolve(p).then(unsubscribe => {
				if (typeof unsubscribe === 'function') {
					unsubscribe();
				}
			});
		});
	};

	// ---------- SPF auto-add through Entri (#15531) ----------
	// A separate process from the SES verification records: preview first (read-only), then Entri with ONE merge record.
	// steps: loading | preview | blocked | disabled | verified | starting | entri | applied | shared | failed
	openSpfEntriPopup = (domainObj) => {
		if (!domainObj?.domain) {
			return;
		}
		this.unsubscribeEntriEvents();
		this.spfEntriPopup = {show: true, domainObj, step: 'loading', preflight: null, result: null, resultError: false, provider: '', setupType: '', errMsg: '', verifying: false};
		this.runSpfEntriPreflight();
	};

	closeSpfEntriPopup = () => {
		this.unsubscribeEntriEvents();
		if (this.spfEntriPopup) {
			this.spfEntriPopup.show = false;
		}
	};

	// message: kept above the (re-loaded) preview, e.g. "the record changed since the preview"
	private runSpfEntriPreflight = (message = '') => {
		const popup = this.spfEntriPopup;
		popup.step = 'loading';
		popup.errMsg = '';
		this.rs.updateDomain(popup.domainObj.domain, {action: this.dic.CONSTANTS.domainsActions.spfEntriPreflight.value}).then(preflight => {
			if (popup !== this.spfEntriPopup) {
				return;
			}
			popup.preflight = preflight;
			popup.step = preflight?.status === 'ready' ? 'preview' : (preflight?.status || 'failed');
			popup.errMsg = message;
			if (preflight?.status === 'verified') {
				popup.domainObj.spf_verified = true;
			}
		}, err => {
			if (popup !== this.spfEntriPopup) {
				return;
			}
			popup.step = 'failed';
			popup.errMsg = err?.data?.message || message;
		});
	};

	startSpfEntri = () => {
		const popup = this.spfEntriPopup;
		if (!popup || popup.step !== 'preview') {
			return;
		}
		popup.step = 'starting';
		const data = {action: this.dic.CONSTANTS.domainsActions.spfEntriStart.value, currentRecord: popup.preflight.current.record};
		this.rs.updateDomain(popup.domainObj.domain, data).then(res => {
			if (popup !== this.spfEntriPopup || !popup.show) {
				return;
			}
			this.subscribeSpfEntriEvents(popup);
			popup.step = 'entri';
			const entriConfig = res.entriConfig;
			connectDomain({
				applicationId: entriConfig.applicationId,
				token: entriConfig.token,
				dnsRecords: entriConfig.dnsRecords, // exactly one TXT record - the SPF merge record
				prefilledDomain: popup.domainObj.domain,
				applicationName: 'Trustifi',
				userId: entriConfig.userId, // same as the token's userId, otherwise Entri forces the manual screen
				overrideSPF: false,     // explicit: Entri MERGES into the existing SPF record, never replaces it
				checkConflicts: false,  // SPF is merged, not overridden - the conflicts screen would only suggest a replacement
				useNewUI: true
			}).catch(() => {
				this.unsubscribeEntriEvents();
				popup.step = 'failed';
			});
		}, err => {
			if (popup !== this.spfEntriPopup) {
				return;
			}
			// the record changed since the preview (or is no longer eligible) - show the fresh preview / reason
			this.runSpfEntriPreflight(err?.data?.message || '');
		});
	};

	private subscribeSpfEntriEvents = (popup) => {
		// real verification + explanation; audited once as the completion of this SPF auto-add
		this.subscribeEntriPopupEvents(popup, () => this.loadSpfEntriResult({setupType: popup.setupType || 'unknown', provider: popup.provider}));
	};

	// Entri window events for a preview-first popup (SPF auto-add, MX change): steps entri -> applied | shared | failed | preview
	private subscribeEntriPopupEvents = (popup, onApplied: () => void) => {
		this.unsubscribeEntriEvents();

		this.entriUnsubscribers.push(subscribe('onSuccess', (event) => {
			if (event.domain !== popup.domainObj.domain) {
				return;
			}
			this.unsubscribeEntriEvents();
			if (!event.success) {
				popup.step = 'failed';
				return;
			}
			popup.provider = event.provider && event.provider !== 'unknown' ? event.provider : '';
			popup.setupType = (event.setupType || '').toLowerCase();
			popup.jobId = event.jobId || ''; // Entri flow id - lets the backend ask Entri whether the records propagated
			if (popup.setupType === 'sharedlogin') {
				// a colleague got the login link - the record is applied when they finish
				popup.step = 'shared';
				return;
			}
			popup.step = 'applied';
			onApplied();
		}));

		this.entriUnsubscribers.push(subscribe('onSharedFlowSent', () => {
			this.unsubscribeEntriEvents();
			popup.step = 'shared';
		}));

		this.entriUnsubscribers.push(subscribe('onEntriClose', (event) => {
			if (popup.step !== 'entri') {
				return;
			}
			this.unsubscribeEntriEvents();
			if (event?.lastStatus === 'FINISHED_SUCCESSFULLY_LINK_SHARED') {
				popup.step = 'shared';
			}
			else if (event?.error) {
				popup.step = 'failed';
				popup.errMsg = event.error.title || event.error.details || '';
			}
			else {
				// closed without applying: nothing changed - back to the step before the window so the admin can retry or cancel
				popup.step = popup.start ? 'confirm' : 'preview';
			}
		}));

		this.entriUnsubscribers.push(subscribe('entriConfigError', () => {
			this.unsubscribeEntriEvents();
			popup.step = 'failed';
		}));
	};

	// data.setupType present only right after Entri (audited); "Verify again" sends no setupType
	private loadSpfEntriResult = (data = {}) => {
		const popup = this.spfEntriPopup;
		if (!popup || popup.verifying) {
			return;
		}
		popup.verifying = true;
		popup.resultError = false;
		this.rs.updateDomain(popup.domainObj.domain, Object.assign({action: this.dic.CONSTANTS.domainsActions.spfEntriResult.value}, data)).then(result => {
			if (popup !== this.spfEntriPopup) {
				return;
			}
			popup.verifying = false;
			popup.result = result;
			popup.domainObj.spf_verified = !!result?.spfVerified;
		}, () => {
			if (popup !== this.spfEntriPopup) {
				return;
			}
			// the verification request itself failed - say so, do not pretend the record is propagating
			popup.verifying = false;
			popup.result = null;
			popup.resultError = true;
		});
	};

	recheckSpfAfterApply = () => {
		this.loadSpfEntriResult();
	};

	// ---------- MX change through Entri (inbound domains page) ----------
	// Moves the MX from a security gateway back to the mail platform (Microsoft 365 / Google Workspace). Preview first
	// (current MX, detected platform, suggested target the admin can confirm or edit), then a confirm step with the impact
	// and the typed domain name; "Open DNS provider" validates on the server (typed name, MX unchanged, Entri token for the
	// MX records only) and opens Entri. steps: loading | preview | blocked | disabled | verified | confirm | starting | entri | applied | shared | failed
	openMxChangePopup = (domainObj) => {
		if (!domainObj?.domain) {
			return;
		}
		this.unsubscribeEntriEvents();
		this.mxChangePopup = {show: true, domainObj, step: 'loading', preflight: null, targetKey: '', customRecords: [{exchange: '', priority: 10}], confirmDomain: '', showNotes: false, start: null, result: null, resultError: false, provider: '', setupType: '', jobId: '', errMsg: '', verifying: false};
		this.runMxChangePreflight();
	};

	closeMxChangePopup = () => {
		this.unsubscribeEntriEvents();
		if (this.mxChangePopup) {
			this.mxChangePopup.show = false;
		}
	};

	// restart: drop a change that reached Entri but never propagated (e.g. manual steps never done) and build a fresh preview
	private runMxChangePreflight = (message = '', restart = false) => {
		const popup = this.mxChangePopup;
		popup.step = 'loading';
		popup.errMsg = '';
		popup.start = null;
		popup.result = null;
		const data: any = {action: this.dic.CONSTANTS.domainsActions.mxChangePreflight.value};
		if (restart) {
			data.restart = true;
		}
		this.rs.updateDomain(popup.domainObj.domain, data).then(preflight => {
			if (popup !== this.mxChangePopup) {
				return;
			}
			popup.preflight = preflight;
			// a change started earlier (popup closed before propagation, or a colleague finished a shared setup): show its result
			if (preflight?.inProgress) {
				popup.start = {target: {records: preflight.inProgress.target}};
				popup.step = 'applied';
				this.loadMxChangeResult();
				return;
			}
			popup.targetKey = preflight?.suggested || (preflight?.customAllowed && !preflight?.candidates?.length ? 'custom' : '');
			popup.confirmDomain = '';
			popup.step = preflight?.status === 'ready' ? 'preview' : (preflight?.status || 'failed');
			popup.errMsg = message;
		}, err => {
			if (popup !== this.mxChangePopup) {
				return;
			}
			popup.step = 'failed';
			popup.errMsg = err?.data?.message || message;
		});
	};

	// the records the admin is about to apply (a detected platform, or the custom list)
	mxTargetRecords = (popup) => {
		if (!popup?.preflight) {
			return [];
		}
		if (popup.targetKey === 'custom') {
			return popup.customRecords.filter(record => record.exchange?.trim());
		}
		return popup.preflight.candidates?.find(itm => itm.key === popup.targetKey)?.records || [];
	};

	// start the custom list from a detected platform's records so the admin can adjust the suggestion instead of retyping it
	editMxCandidate = (popup, candidate) => {
		popup.customRecords = candidate.records.map(record => ({exchange: record.exchange, priority: record.priority}));
		popup.targetKey = 'custom';
	};

	addMxCustomRecord = (popup) => {
		if (popup.customRecords.length < 5) {
			popup.customRecords.push({exchange: '', priority: 10});
		}
	};

	removeMxCustomRecord = (popup, index) => {
		popup.customRecords.splice(index, 1);
		if (!popup.customRecords.length) {
			popup.customRecords.push({exchange: '', priority: 10});
		}
	};

	// preview: a target is chosen and, for custom records, every row has a host and an explicit priority (an empty priority is not 0)
	mxTargetReady = (popup) => {
		const records = this.mxTargetRecords(popup);
		const complete = popup?.targetKey !== 'custom' || records.every(record => record.priority !== null && record.priority !== undefined && String(record.priority).trim() !== '' && Number.isInteger(Number(record.priority)));
		return !!popup?.preflight && !!popup.targetKey && records.length > 0 && complete;
	};

	// confirm step: the target is ready and the admin typed the domain name
	canStartMxChange = (popup) => {
		return popup?.step === 'confirm' && this.mxTargetReady(popup) && (popup.confirmDomain || '').trim().toLowerCase() === popup.domainObj.domain;
	};

	mxTargetName = (popup) => {
		return popup?.targetKey === 'custom' ? 'Custom MX records' : (popup?.preflight?.candidates?.find(itm => itm.key === popup.targetKey)?.name || '');
	};

	mxNotices = (popup, level) => {
		return (popup?.preflight?.notices || []).filter(notice => (level === 'warning') === (notice.level === 'warning'));
	};

	// the popup buttons per step; the same object is returned until the step / form state changes (an object literal in the
	// template was rebuilt on every keystroke, which made the dynamic-size popup jump - QA 2026-10-25)
	private mxButtonsCache = {key: '', value: null};
	mxChangeButtons = (popup) => {
		const key = [popup?.step, this.mxTargetReady(popup), this.canStartMxChange(popup), popup?.verifying, popup?.result?.status, popup?.result?.propagated].join('|');
		if (this.mxButtonsCache.key !== key) {
			const step = popup?.step;
			let confirm = null;
			if (step === 'preview') {
				confirm = {text: 'Continue', disabled: !this.mxTargetReady(popup)};
			}
			else if (step === 'confirm' || step === 'starting') {
				confirm = {text: 'Open DNS provider', disabled: step === 'confirm' && !this.canStartMxChange(popup), loading: step === 'starting'};
			}
			else if (step === 'applied' && !(popup.result?.status === 'applied' && popup.result?.propagated)) {
				confirm = {text: 'Check again', loading: !!popup.verifying};
			}
			this.mxButtonsCache = {key, value: {cancel: ['preview', 'loading', 'confirm', 'starting', 'entri'].includes(step) ? 'Cancel' : 'Close', confirm}};
		}
		return this.mxButtonsCache.value;
	};

	mxChangeConfirm = () => {
		const popup = this.mxChangePopup;
		if (!popup) {
			return;
		}
		if (popup.step === 'preview') {
			if (this.mxTargetReady(popup)) {
				popup.confirmDomain = '';
				popup.errMsg = '';
				popup.step = 'confirm';
			}
		}
		else if (popup.step === 'confirm') {
			this.startMxChange();
		}
		else if (popup.step === 'applied') {
			this.recheckMxAfterApply();
		}
	};

	backToMxPreview = () => {
		const popup = this.mxChangePopup;
		if (popup?.step === 'confirm') {
			popup.step = 'preview';
		}
	};

	// confirm step -> server start (typed name, MX unchanged since the preview, target validation, Entri token) -> Entri window
	startMxChange = () => {
		const popup = this.mxChangePopup;
		if (!this.canStartMxChange(popup)) {
			return;
		}
		popup.step = 'starting';
		popup.errMsg = '';
		const target: any = {key: popup.targetKey};
		if (popup.targetKey === 'custom') {
			target.records = this.mxTargetRecords(popup).map(record => ({exchange: record.exchange.trim(), priority: Number(record.priority)}));
		}
		const data = {
			action: this.dic.CONSTANTS.domainsActions.mxChangeStart.value,
			confirmDomain: popup.confirmDomain.trim(),
			currentMx: popup.preflight.current.records,
			target
		};
		this.rs.updateDomain(popup.domainObj.domain, data).then(res => {
			if (popup !== this.mxChangePopup || !popup.show) {
				return;
			}
			popup.start = res;
			this.openMxEntriWindow();
		}, err => {
			if (popup !== this.mxChangePopup) {
				return;
			}
			const message = err?.data?.message || '';
			// a changed MX reloads the preview; a refused target goes back to the records with the message
			if (/changed since the preview|not available/i.test(message)) {
				this.runMxChangePreflight(message);
				return;
			}
			popup.step = 'preview';
			popup.errMsg = message;
		});
	};

	// right after a successful start: the Entri window, with the token minted for exactly these MX records on this domain
	private openMxEntriWindow = () => {
		const popup = this.mxChangePopup;
		if (!popup || popup.step !== 'starting' || !popup.start?.entriConfig) {
			return;
		}
		// after the Entri window: the real zone check + Entri's flow status decide what we show; audited once as the completion
		this.subscribeEntriPopupEvents(popup, () => this.loadMxChangeResult({setupType: popup.setupType || 'unknown', provider: popup.provider, jobId: popup.jobId}));
		popup.step = 'entri';
		const entriConfig = popup.start.entriConfig;
		connectDomain({
			applicationId: entriConfig.applicationId,
			token: entriConfig.token,
			dnsRecords: entriConfig.dnsRecords, // the MX records only
			prefilledDomain: popup.domainObj.domain,
			applicationName: 'Trustifi',
			userId: entriConfig.userId, // same as the token's userId, otherwise Entri forces the manual screen
			checkConflicts: true,   // Entri shows the existing MX records it is about to replace before the admin confirms
			useNewUI: true
		}).catch(() => {
			this.unsubscribeEntriEvents();
			popup.step = 'failed';
		});
	};

	// data.setupType / jobId present only right after Entri (audited); "Check again" sends neither
	private loadMxChangeResult = (data = {}) => {
		const popup = this.mxChangePopup;
		if (!popup || popup.verifying) {
			return;
		}
		popup.verifying = true;
		popup.resultError = false;
		this.rs.updateDomain(popup.domainObj.domain, Object.assign({action: this.dic.CONSTANTS.domainsActions.mxChangeResult.value}, data)).then(result => {
			if (popup !== this.mxChangePopup) {
				return;
			}
			popup.verifying = false;
			popup.result = result;
		}, () => {
			if (popup !== this.mxChangePopup) {
				return;
			}
			popup.verifying = false;
			popup.result = null;
			popup.resultError = true;
		});
	};

	recheckMxAfterApply = () => {
		this.loadMxChangeResult();
	};

	restartMxChange = () => {
		this.runMxChangePreflight('', true);
	};

	mxEvidenceText = (candidate) => {
		return (candidate?.evidence || []).map(itm => itm.text).join('; ');
	};

	// SPF "all" qualifier -> label. null = the record has no "all" term, which RFC 7208 treats as neutral (same as "?all")
	spfPolicyLabel = (qualifier) => {
		if (!qualifier) {
			return 'no "all" term (neutral)';
		}
		return `"${qualifier === '+' ? '' : qualifier}all"`;
	};

	spfPolicySame = (before, after) => (before || '?') === (after || '?');

	// preview: the merged record split around the added include so the template can highlight it
	spfExpectedParts = (record) => {
		const include = 'include:_spf.trustifi.com';
		const idx = (record || '').indexOf(include);
		return idx === -1 ? [record || '', '', ''] : [record.slice(0, idx), include, record.slice(idx + include.length)];
	};

    isDomainIdentityVerified(domainObj) {
    	return this.verifyDomainRecord(domainObj, this.dic.CONSTANTS.domainVerify.identity).then(isVerified => {
            if (isVerified !== null) {
                domainObj.verified = isVerified;
            }
        });
    };

    isDomainDkimVerified(domainObj) {
    	return this.verifyDomainRecord(domainObj, this.dic.CONSTANTS.domainVerify.dkim).then(isVerified => {
            if (isVerified !== null) {
                domainObj.dkim_verified = isVerified;
            }
        });
    };

	isDomainSpfVerified(domainObj) {
		return this.verifyDomainRecord(domainObj, this.dic.CONSTANTS.domainVerify.spf).then(isVerified => {
			if (isVerified !== null) {
				domainObj.spf_verified = isVerified;
			}
		});
	};

    isDomainMailFromVerified(domainObj) {
    	return this.verifyDomainRecord(domainObj, this.dic.CONSTANTS.domainVerify.mailFrom).then(isVerified => {
            if (isVerified !== null) {
                domainObj.mail_from_verified = isVerified;
            }
        });
    };

    addDomain(domain) {
        if (!domain) {
            return;
        }
		this.addActiveDomainPopup.applyInProcess = true;
        let duplicateDomain = _.find<any>(this.userDomains, d => d.domain === domain);
		// CC: remember the "Auto verify" checkbox before the popup is closed/reset
		const autoVerify = this.addActiveDomainPopup.autoVerify;
        this.rs.addDomain(domain).then((response) => {
            this.userDomains = response;
			this.addActiveDomainPopup.applyInProcess = false;
			this.addActiveDomainPopup.show = false;

			// CC: run the Auto Verify (Entri) flow on the newly added domain when requested
			if (autoVerify) {
				const newDomainObj = _.find<any>(this.userDomains, d => d.domain === domain);
				if (newDomainObj) {
					this.connectEntriDomain(newDomainObj);
				}
			}
        }, err => {
            if (err.data && err.data) {
                if (!duplicateDomain && this.userDomains.length >= 5) {
					this.addActiveDomainPopup.applyInProcess = false;
					return;
                } else {
                    this.addActiveDomain(this.domain, err.data.message);
					this.addActiveDomainPopup.applyInProcess = false;
                }
            }
        });
    };

    getAmazonDomainConfigString(domainObj) {
        let data = domainObj.data;
        let records = [
            `trustifi.trustificorp.info 3600 MX 10 feedback-smtp.us-east-1.amazonses.com`,
            `_amazonses.trustificorp.info 3600 TXT "${data.verify_token}"`,
            `trustifi.trustificorp.info 3600 TXT "v=spf1 include:amazonses.com ~all"`,
            `${data.dkim[0]}._domainkey.trustificorp.info 3600 CNAME ${data.dkim[0]}.dkim.amazonses.com`,
            `${data.dkim[1]}._domainkey.trustificorp.info 3600 CNAME ${data.dkim[1]}.dkim.amazonses.com`,
            `${data.dkim[2]}._domainkey.trustificorp.info 3600 CNAME ${data.dkim[2]}.dkim.amazonses.com`
        ]

        return records.join('\n');
    };

    copyAmazonDomainConfigString() {
        this.clipboard.copy(this.amazonVerificationPopup.configString);
    }

    openAmazonVerificationPopup(domainObj) {
        this.amazonVerificationPopup =  {
            domainObj,
            configString: this.getAmazonDomainConfigString(domainObj),
            show: true
        };
    };

    getDomainDkimAndTokenCsv(domain) {
        if (!domain) {
            return;
        }

        let csvString = "TXT Records:\n";
        csvString += 'Host,Type,Value\n';
        csvString += `_amazonses,TXT,${domain.data.verify_token}\n`;
        csvString += `trustifi,TXT,"v=spf1 include:amazonses.com ~all"\n`;
        csvString += '\n';

        csvString += 'CNAME Records:\n';
        csvString += 'Host,Type,Pointer\n';
        domain.data.dkim.forEach((dkim) => {
            csvString += `${dkim}._domainkey,CNAME,${dkim}.dkim.amazonses.com\n`;
        });
        csvString += '\n';

        csvString += 'MX Records:\n';
        csvString += 'Host,Type,Pointer,Priority\n';
        csvString += `trustifi,MX,feedback-smtp.us-east-1.amazonses.com,10\n`;

        this.gs.exportCsv(csvString, `domain_ ${domain.domain}_data.csv`);
    };

    emailForwardingChange = (domainObj) => {
        // CC: the toggle in the DNS records popup already flipped the model to the target value
        const targetEnabled = domainObj.email_feedback_forwarding;
        this.gs.showPopup({
            title: 'Email feedback forwarding',
            subTitle: `You are about to ${targetEnabled ? 'enable' : 'disable'} email feedback forwarding for ${domainObj.domain}`,
            body: [
            ],
            type: this.dic.CONSTANTS.popupInfo,
            doneBtnText: 'Apply',
            doneCb: () => {
                const updateData = {
                    enable: targetEnabled,
                    action: this.dic.CONSTANTS.domainsActions.emailForwardingEnable.value
                };
                this.rs.updateDomain(domainObj.domain, updateData).then(res => {
                    this.ns.showInfoMessage(this.dic.MESSAGES.domainUpdate);
                }, err => {
                    // CC: revert the toggle if the update failed
                    domainObj.email_feedback_forwarding = !targetEnabled;
                });
            },
            cancelCb: () => {
                // CC: revert the toggle if the user cancels
                domainObj.email_feedback_forwarding = !targetEnabled;
            }
        });
    };

    openAssignDomainToAdminPopup(domainObj) {
		const planAdmins = _.cloneDeep(this.gs.planAdmins);
		planAdmins.shift(); // remove partner admin
        if (!planAdmins?.length) {
            this.ns.showWarnMessage(this.dic.ERRORS.domainsNoOtherAdmins);
            return;
        }

        planAdmins.forEach(admin => {
            admin['formattedDisplay'] = (admin.user_id?.name || '') + ' (' + admin.email + ')';
            admin.hide = false;
        });

        this.assignDomainToAdminPopup = {
            domainToAssign: domainObj,
            adminsList: planAdmins,
            selectedAdmin: null,
            show: true
        };
    };

    assignDomainToAdmin() {
        if (this.assignToAdminInProcess) {
            return;
        }
        if (!this.assignDomainToAdminPopup.selectedAdmin) {
            this.ns.showWarnMessage(util.format(this.dic.ERRORS.noSelected, 'admin'));
            return;
        }

        this.assignToAdminInProcess = true;
        const selectedAdmin = this.assignDomainToAdminPopup.selectedAdmin;

        const assignData = {
            email: selectedAdmin.user_id.email,
            action: this.dic.CONSTANTS.domainsActions.assignDomain.value
        };

        this.rs.updateDomain(this.assignDomainToAdminPopup.domainToAssign.domain, assignData).then(response => {
            this.assignDomainToAdminPopup.domainToAssign.assigned_to = selectedAdmin.user_id.email;

            if (!this.assignAdmins.includes(selectedAdmin.user_id.email)) {
                this.assignAdmins.push(selectedAdmin.user_id.email);
            }
            this.initFilters();

            this.assignDomainToAdminPopup = null;
            this.assignToAdminInProcess = false;
        }, err => {
            if (err && err.data && err.data.message && !err.data.display_bar) {
                this.ns.showErrorMessage(err.data.message);
            }
            this.assignToAdminInProcess = false;
        });
    };

    unassignFromAdmin = (domainObj) => {
        if (this.assignToAdminInProcess) return;
		this.unassignFromAdminPopup = {
			show: true,
			subTitle: `Please note - you are about to unassign a domain from ${domainObj.assigned_to}`,
			verifyDomain: {
					equalsTo: domainObj.domain,
					model: ''
				},
			bodyDetails: [
				`All email accounts under this domain will not be verified for admin ${domainObj.assigned_to}`,
				`In case ${domainObj.assigned_to} is connected to Email Relay, they will no longer be able to use it`
			],
			doneCb: () => {
				this.assignToAdminInProcess = true;

				const assignData = {
					email: domainObj.assigned_to,
					action: this.dic.CONSTANTS.domainsActions.unassignDomain.value
				};
				this.rs.updateDomain(domainObj.domain, assignData).then(res => {
					domainObj.assigned_to = '';
					this.assignToAdminInProcess = false;
					this.unassignFromAdminPopup.show = false;
				}, err => {
					this.assignToAdminInProcess = false;
				});
			}
		}
    };

    exportToCsv = (sortBy) => {
        if (!this.userDomains || !this.userDomains.length) {
            this.ns.showWarnMessage(this.dic.ERRORS.noDataToExportCsv);
            return;
        }

        let csvString = "Domain,Status,DKIM,MAIL FROM, Email feedback forwarding, SPF, Created,Assigned To\n";

        let sortedTable = _.filter(this.userDomains,domain => {return !domain.hide});
        if (sortBy) {
            sortedTable = this.gs.sortTable(sortedTable, sortBy);
        }

        sortedTable.forEach((domain) => {
            csvString += `${domain.domain},"${domain.verified ? 'Can send' : 'Cannot send'}","${domain.dkim_verified ? 'Verified' : 'Unverified'}","${domain.mail_from_verified ? 'Verified' : 'Unverified'}","${domain.email_feedback_forwarding ? 'Enabled' : 'Disabled'}","${domain.spf_verified ? 'Verified' : 'Unverified'}",${domain.created},"${domain.assigned_to || 'Your plan'}"\n`;
        });

        this.gs.exportCsv(csvString, `domains.csv`);
    };

    searchDomain = (searchTerm, activeFilters) => {
        this.userDomains.forEach(record => {
            // search
            if (searchTerm) {
                const isFound = searchTextExecute(record, searchTerm);
                if (!isFound) {
                    record.hide = true;
                    return;
                }
            }

            // filter
            if (activeFilters) {
                // need to match all filter types
                let numFilterToMatch = Object.keys(activeFilters).length;

                if (activeFilters.status && activeFilters.status.length) {
                    if (activeFilters.status.includes(record.verified ? 'can send' : 'cannot send')) {
                        numFilterToMatch--;
                    }
                }

                if (activeFilters.dkim && activeFilters.dkim.length) {
                    if (activeFilters.dkim.includes(record.dkim_verified ? 'verified' : 'unverified')) {
                        numFilterToMatch--;
                    }
                }
				if (activeFilters.spf && activeFilters.spf.length) {
					if (activeFilters.spf.includes(record.spf_verified ? 'verified' : 'unverified')) {
						numFilterToMatch--;
					}
				}
                if (activeFilters['mail from'] && activeFilters['mail from'].length) {
                    if (activeFilters['mail from'].includes(record.mail_from_verified ? 'verified' : 'unverified')) {
                        numFilterToMatch--;
                    }
                }
                if (activeFilters['Email feedback forwarding'] && activeFilters['Email feedback forwarding'].length) {
                    if (activeFilters['Email feedback forwarding'].includes(record.email_feedback_forwarding ? 'enabled' : 'disabled')) {
                        numFilterToMatch--;
                    }
                }
                if (activeFilters['assigned to'] && activeFilters['assigned to'].length) {
                    if (activeFilters['assigned to'].includes(record.assigned_to ? record.assigned_to : 'your plan')) {
                        numFilterToMatch--;
                    }
                }
                if (numFilterToMatch) {
                    record.hide = true;
                    return;
                }
            }

            record.hide = false;
        });
    };

    initFilters = () => {
        this.filterData = {
            filterType: this.dic.CONSTANTS.tableFilters.domains,
            filters: {
                status: ['can send', 'cannot send'],
                dkim: ['verified', 'unverified'],
				spf: ['verified', 'unverified'],
				'mail from': ['verified', 'unverified'],
                'Email feedback forwarding': ['enabled', 'disabled'],
                'assigned to': this.assignAdmins
            },
            initFiltersFunction: this.initFilters,
        };
    }
}

function searchTextExecute(domainObj, searchTerm) {
    searchTerm = searchTerm.toLowerCase();
    return ((domainObj.domain && domainObj.domain.toLowerCase().indexOf(searchTerm) > -1) ||
        (domainObj.assigned_to && domainObj.assigned_to.toLowerCase().indexOf(searchTerm) > -1) ||
        (!domainObj.assigned_to && "Your plan".toLowerCase().indexOf(searchTerm) > -1));
}
